import type { District, DistrictGate, WorldManifest } from "@townsy/contracts";
import { districtItems } from "./districtItems";
import { HILLCREST } from "./districts/hillcrest";
import { RIVERSIDE } from "./districts/riverside";
import { SPORTS_PARK_ID } from "./districts/sportsPark";
import { DISTRICT_ID, DISTRICT_STREETS, WORLD_ID, WORLD_VERSION, type StreetInfo } from "./manifest";
import { trafficLoops, type LoopPath } from "./traffic";

/**
 * World v5 (ADR-0041): one district's slice of the manifest. It is a WorldManifest (so every function that takes one
 * works on it) whose only district is `district`, with that district's plots, billboards, surfaces, props and
 * colliders (manifest order kept), the shared interiors and movement settings, plus its named streets and its own
 * traffic loops (junction zones included, computed on first use).
 *
 * World v6 (ADR-0042, one continuous town): the town is ONE street room built from the WHOLE manifest, and the
 * street-geometry functions handed the whole manifest mean the whole town (streetColliders, streetDecor, streetSeats,
 * drivableRects, trafficLoops; town.ts has the same calls from a slice or the whole manifest: townColliders, ...). The
 * slices stay for per-district data the HTML UI uses (a district's plots, streets, billboards, square, overview
 * framing, the minimap's "Here" view) and for per-district checks; a slice's colliders are that district's alone (its
 * walls open where Market Street comes in) and its traffic loops are its own roads' (cars of the town street room drive
 * townTrafficLoops). Plot lookups keep the whole manifest.
 */
export interface DistrictManifest extends WorldManifest {
  /** The slice's one district (also `districts[0]`). */
  readonly district: District;
  /** Its named streets (the minimap, the directory, street labels). */
  readonly streets: readonly StreetInfo[];
  /**
   * The loops of the district's own roads (trafficLoops of this slice), cached on the slice. World v6: the town's street
   * room drives townTrafficLoops (town.ts), whose cars cross between the districts; these are per-district only.
   */
  readonly trafficLoops: readonly LoopPath[];
}

const SLICES = new WeakMap<WorldManifest, Map<string, DistrictManifest>>();
/** Each slice's whole manifest (so a slice can be asked for another district, or a property / point lookup). */
const WHOLE = new WeakMap<object, WorldManifest>();

/** True for an object made by districtManifest. */
export function isDistrictManifest(m: WorldManifest): m is DistrictManifest {
  return WHOLE.has(m);
}

/** World v6: the whole manifest (the whole town) of a district slice, or `m` itself. */
export function townManifest(m: WorldManifest): WorldManifest {
  return WHOLE.get(m) ?? m;
}

/** Named streets of a district of WORLD_MANIFEST (a manifest of another world or version has none listed). */
function streetsOf(m: WorldManifest, districtId: string): readonly StreetInfo[] {
  return m.worldId === WORLD_ID && m.version === WORLD_VERSION ? (DISTRICT_STREETS.get(districtId) ?? []) : [];
}

/**
 * The slice of district `districtId` (cached per manifest object; asking a slice for another district answers from
 * its whole manifest). Throws for an unknown district.
 */
export function districtManifest(m: WorldManifest, districtId: string): DistrictManifest {
  const whole = townManifest(m);
  let byId = SLICES.get(whole);
  if (!byId) {
    byId = new Map();
    SLICES.set(whole, byId);
  }
  const cached = byId.get(districtId);
  if (cached) return cached;
  const items = districtItems(whole, districtId);
  const slice = { ...items, district: items.districts[0]!, streets: streetsOf(whole, districtId) } as DistrictManifest;
  let loops: LoopPath[] | null = null;
  // Not enumerable: comparing or serialising a slice never computes the loops.
  Object.defineProperty(slice, "trafficLoops", { enumerable: false, get: () => (loops ??= trafficLoops(slice)) });
  WHOLE.set(slice, whole);
  byId.set(districtId, slice);
  return slice;
}

/** `m` itself when it is a slice, else the slice of its first district (Central for the whole manifest). */
export function asDistrictSlice(m: WorldManifest): DistrictManifest {
  return districtManifest(m, m.districts[0]!.id);
}

/** The named streets of a district (Central's are WORLD_STREETS). */
export function worldStreets(m: WorldManifest, districtId: string): readonly StreetInfo[] {
  return districtManifest(m, districtId).streets;
}

/**
 * Traffic cars per loop of each district SLICE's own loops (its `trafficLoops`), for per-district tools such as the
 * harness. World v6 (ADR-0042): the town's one street room drives townTrafficLoops with TOWN_FLEET (= SERVER_FLEET,
 * traffic.ts); these are no longer what the server runs. Central's slice: its v3 loops' 21 cars, 6 on Market Street
 * between the avenues and 3 on each avenue loop that ends at Central's edge (v5: Central's 29, [10, 2, 2, 2, 2, 3, 8]);
 * Riverside and Hillcrest: 6 on the shopping street, 2 on each lane (nearer Central first) and 3 on the back road, as in
 * v5; the Sports Park (ADR-0044): 4 on Stadium Way and 1 on its stretch of Arena Avenue (Central's stretch has no loop in
 * Central's slice; in the town one loop drives both). Car ids are 0..n-1 per fleet.
 */
export const DISTRICT_FLEETS: Readonly<Record<string, readonly number[]>> = {
  [DISTRICT_ID]: [10, 2, 2, 2, 2, 3, 6, 3, 3],
  [RIVERSIDE.id]: [6, 2, 2, 3],
  [HILLCREST.id]: [6, 2, 2, 3],
  [SPORTS_PARK_ID]: [4, 1],
};

/** The district of a property (its plot's `districtId`), or null for an id the manifest does not know. */
export function districtOfProperty(m: WorldManifest, propertyId: string): string | null {
  return townManifest(m).plots.find((p) => p.id === propertyId)?.districtId ?? null;
}

/**
 * The district whose bounds contain a point, or null outside every district. Neighbouring districts share their bounds
 * line, and a point exactly on it counts for the district listed first (Central). World v6 (ADR-0042): the visitor's
 * district for the HUD, the map, the directory and the residents is derived from their position with this. On the road
 * between two districts the boundary is that shared line, Central's edge x = +-312 (the v5 wall and gate line): Market
 * Street (Central) up to and including it, River Street (Riverside) east of it, High Street (Hillcrest) west of it.
 * ADR-0044: on Arena Avenue the line is Central's north edge z = 150: up to and on it Central, north of it the Sports
 * Park. A point inside the town's bounds but outside every district (behind a district's closed wall) is null.
 */
export function districtOfPoint(m: WorldManifest, x: number, z: number): string | null {
  const d = townManifest(m).districts.find((b) => x >= b.bounds.minX && x <= b.bounds.maxX && z >= b.bounds.minZ && z <= b.bounds.maxZ);
  return d?.id ?? null;
}

/**
 * A gate by id, with the district it is in, or null. World v6 (ADR-0042): the town has no gates (every district's
 * `gates` is empty), so this is always null; kept until the realtime room (ADR-0042 slice 2) drops its gate code.
 * @deprecated World v6 has no gates.
 */
export function districtGate(m: WorldManifest, gateId: string): { district: District; gate: DistrictGate } | null {
  for (const district of townManifest(m).districts) {
    const gate = district.gates.find((g) => g.id === gateId);
    if (gate) return { district, gate };
  }
  return null;
}
